import { Button, Input, Progress, message } from 'antd';
import { Upload as UploadIcon, X } from 'lucide-react';
import {
  useCallback,
  useRef,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { resolveAssetUrl } from '@shared/utils/assetUrl';
import { fmtBytes, mockUpload } from './helpers';

/**
 * A "plausible image URL" sanity check. We bail out of the preview rendering
 * path and fall back to the upload DropZone when the bound value looks like
 * something that can't possibly resolve to an image (hex color, free-text,
 * etc.). Seed data in the BE has surfaced corrupted values like "#E6ECF7"
 * for image columns; we treat those as "no image" so the user sees the
 * uploader instead of a broken-thumb tile with only a remove button.
 */
function looksLikeImageUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  return /^(https?:\/\/|\/|data:image\/|blob:)/i.test(url);
}

export interface MediaUploadValue {
  url: string;
  name?: string;
  size?: number;
  alt?: string;
  mime?: string;
  /**
   * BE dosya id'si (varsa). MediaPicker upload sekmesi bunu kullanıp taze yüklenen
   * dosyanın güvenlik tarama durumunu sorgular; kütüphane sekmesiyle aynı gate.
   */
  id?: string;
}

interface BaseProps {
  accept?: string;
  maxSizeMB?: number;
  /**
   * Show the per-image alt-text input. Default `false`. Most consumers don't
   * persist alt (Brand.LogoUrl, SEO og:image, etc.) so the input is hidden by
   * default to avoid letting users type into a value that gets silently
   * dropped. Pass `showAlt` only where alt actually round-trips to the BE.
   */
  showAlt?: boolean;
  /** Implies `showAlt`. Adds "(zorunlu)" placeholder + red border when empty. */
  altTextRequired?: boolean;
  uploader?: (file: File, onProgress?: (n: number) => void) => Promise<MediaUploadValue>;
  hint?: ReactNode;
  disabled?: boolean;
}

interface SingleProps extends BaseProps {
  multi?: false;
  value: MediaUploadValue | null;
  onChange: (next: MediaUploadValue | null) => void;
}

interface MultiVariantProps extends BaseProps {
  multi: true;
  value: MediaUploadValue[];
  onChange: (next: MediaUploadValue[]) => void;
  maxFiles?: number;
}

type MediaUploadProps = SingleProps | MultiVariantProps;

function DropZone({
  accept,
  disabled,
  onFiles,
  hint,
  multi,
}: {
  accept?: string;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  hint?: ReactNode;
  multi?: boolean;
}) {
  const { t } = useTranslation('components');
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDrag(false);
    if (disabled) return;
    onFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <label
      className="ds-media-drop"
      data-dragging={drag ? 'true' : 'false'}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      aria-disabled={disabled}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multi}
        disabled={disabled}
        onChange={(e) => {
          if (e.target.files) onFiles(Array.from(e.target.files));
          e.target.value = '';
        }}
      />
      <div className="ds-media-drop__icon">
        <UploadIcon size={20} />
      </div>
      <div>{t('mediaUpload.dropzone')}</div>
      <div className="ds-media-drop__hint">{hint ?? t('mediaUpload.maxSizeDefault')}</div>
    </label>
  );
}

function SingleMedia({
  value,
  onChange,
  accept = 'image/*',
  maxSizeMB = 5,
  showAlt,
  altTextRequired,
  uploader,
  hint,
  disabled,
}: SingleProps) {
  const { t } = useTranslation('components');
  const altVisible = showAlt || altTextRequired;
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  // Track the resolved src that failed to load. When the bound URL changes
  // (typically because the user just uploaded a new file), reset the flag so
  // the fresh upload gets a fair load attempt.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  // Effect'siz "adjust state on prop change" deseni: bağlı URL değişince
  // (tipik olarak yeni upload) fail bayrağını render sırasında sıfırla.
  const [prevUrl, setPrevUrl] = useState(value?.url);
  if (value?.url !== prevUrl) {
    setPrevUrl(value?.url);
    setFailedSrc(null);
  }

  const handleFiles = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;
      if (file.size > maxSizeMB * 1024 * 1024) {
        void message.error(t('mediaUpload.fileTooLarge', { max: maxSizeMB }));
        return;
      }
      setBusy(true);
      setProgress(0);
      try {
        const result = await (uploader ?? mockUpload)(file, setProgress);
        onChange(result);
      } catch (err) {
        // filesUploader rejects when the async ClamAV scan fails or times out.
        // Surface its Turkish message instead of leaking an unhandled rejection.
        void message.error(err instanceof Error ? err.message : t('mediaUpload.uploadFailed'));
      } finally {
        setBusy(false);
      }
    },
    [maxSizeMB, onChange, uploader, t],
  );

  // Plausible-URL check rejects garbage values like hex colors that came in
  // from corrupted seed data. onError + failedSrc rejects URLs that resolve
  // syntactically but can't actually be loaded (404, deleted blob, CORS).
  // Both cases route the user back to the uploader instead of a misleading
  // "broken preview tile with only a remove button" state.
  const resolvedUrl = value?.url ? resolveAssetUrl(value.url) : null;
  const usable =
    value !== null && looksLikeImageUrl(value?.url) && failedSrc !== resolvedUrl;

  if (!usable || !value || !resolvedUrl) {
    return (
      <div className="ds-media-upload">
        <DropZone
          accept={accept}
          disabled={disabled || busy}
          onFiles={(f) => void handleFiles(f)}
          hint={hint ?? t('mediaUpload.maxSize', { max: maxSizeMB })}
        />
        {busy ? <Progress percent={progress} size="small" status="active" /> : null}
      </div>
    );
  }

  return (
    <div className="ds-media-upload">
      <div className="ds-media-preview">
        <div
          className="ds-media-preview__thumb"
          role="img"
          aria-label={value.alt ?? value.name ?? t('mediaUpload.previewLabel')}
        >
          <img src={resolvedUrl} alt="" onError={() => setFailedSrc(resolvedUrl)} />
        </div>
        <div>
          <div className="ds-media-preview__name">{value.name ?? t('mediaUpload.mediaName')}</div>
          <div className="ds-media-preview__meta">{fmtBytes(value.size)}</div>
          {altVisible ? (
            <Input
              size="small"
              style={{ marginTop: 6 }}
              placeholder={altTextRequired ? t('mediaUpload.altPlaceholderRequired') : t('mediaUpload.altPlaceholder')}
              value={value.alt ?? ''}
              onChange={(e) => onChange({ ...value, alt: e.target.value })}
              status={altTextRequired && !value.alt ? 'error' : undefined}
            />
          ) : null}
        </div>
        <Button
          type="text"
          danger
          icon={<X size={14} />}
          onClick={() => onChange(null)}
          aria-label={t('mediaUpload.remove')}
        />
      </div>
    </div>
  );
}

function MultiMedia({
  value,
  onChange,
  accept = 'image/*',
  maxSizeMB = 5,
  maxFiles = 10,
  showAlt,
  altTextRequired,
  uploader,
  hint,
  disabled,
}: MultiVariantProps) {
  const { t } = useTranslation('components');
  const altVisible = showAlt || altTextRequired;
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback(
    async (files: File[]) => {
      if (value.length + files.length > maxFiles) {
        void message.error(t('mediaUpload.maxFilesError', { max: maxFiles }));
        return;
      }
      const next: MediaUploadValue[] = [...value];
      setBusy(true);
      try {
        for (const file of files) {
          if (file.size > maxSizeMB * 1024 * 1024) {
            void message.error(t('mediaUpload.fileTooLargeSkipped', { name: file.name, max: maxSizeMB }));
            continue;
          }
          setProgress(0);
          const result = await (uploader ?? mockUpload)(file, setProgress);
          next.push(result);
        }
      } catch (err) {
        // A scan failure/timeout on one file aborts the batch — surface the
        // error but still commit the files that uploaded cleanly before it.
        void message.error(err instanceof Error ? err.message : t('mediaUpload.uploadFailed'));
      } finally {
        setBusy(false);
        onChange(next);
      }
    },
    [value, maxFiles, maxSizeMB, uploader, onChange, t],
  );

  return (
    <div className="ds-media-upload">
      {value.length < maxFiles ? (
        <DropZone
          accept={accept}
          disabled={disabled || busy}
          multi
          onFiles={(f) => void handleFiles(f)}
          hint={hint ?? t('mediaUpload.maxFilesHint', { maxFiles, maxSize: maxSizeMB })}
        />
      ) : null}
      {busy ? <Progress percent={progress} size="small" status="active" /> : null}
      {value.length > 0 ? (
        <div className="ds-media-grid">
          {value.map((item, idx) => (
            <div key={idx} className="ds-media-tile" data-selected="false">
              {item.url ? <img src={resolveAssetUrl(item.url)} alt={item.alt ?? item.name ?? ''} /> : null}
              <div className="ds-media-tile__caption">
                <div>{item.name}</div>
                {altVisible ? (
                  <Input
                    size="small"
                    placeholder={altTextRequired ? t('mediaUpload.altShortPlaceholderRequired') : t('mediaUpload.altPlaceholder')}
                    value={item.alt ?? ''}
                    onChange={(e) => {
                      const next = [...value];
                      next[idx] = { ...item, alt: e.target.value };
                      onChange(next);
                    }}
                    status={altTextRequired && !item.alt ? 'error' : undefined}
                    style={{ marginTop: 4 }}
                  />
                ) : null}
              </div>
              <Button
                type="text"
                danger
                size="small"
                icon={<X size={12} />}
                style={{ position: 'absolute', top: 4, right: 4 }}
                onClick={() => onChange(value.filter((_, i) => i !== idx))}
                aria-label={t('mediaUpload.remove')}
              />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function MediaUpload(props: MediaUploadProps) {
  if (props.multi) return <MultiMedia {...props} />;
  return <SingleMedia {...props} />;
}
